import { useMemo } from 'react'
import useQueryParams from 'hooks/useQueryParams'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'
import useOfferList from 'hooks/Offers/useOfferList'
import { ELIGIBLE_AD_GROUP_IDS } from 'constants/salesCtrRankingAdGroups'
import Places from 'constants/places'
import {
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_BED_BANK,
  OFFER_TYPE_BUNDLE_AND_SAVE,
} from 'constants/offer'

const TOP_N = 5
const HOTEL_OFFER_TYPES: Array<App.OfferListOfferType> = [
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_BED_BANK,
  OFFER_TYPE_BUNDLE_AND_SAVE,
]
const DIFFERENCE_THRESHOLD = 0.3
const BALI_DESTINATION_ID = Places.Bali.id

/**
 * Returns a 0–1 score measuring how different two ranked lists are.
 * Combines two signals:
 *  - Overlap: how many IDs in topA are missing from topB (e.g. 3/10 missing = 0.3)
 *  - Displacement: how far shared IDs moved in position (normalised by TOP_N²)
 *
 * Example with TOP_N=5:
 *   idsA = [A, B, C, D, E], idsB = [A, C, B, F, E]
 *   Missing from B: D (1/5 = 0.2 overlap score)
 *   Displacement: A=0, B moved 1, C moved 1, D missing=5, E=0 → 7/25 = 0.28
 *   Final score: (0.2 + 0.28) / 2 = 0.24
 */
export function computeRankingDifference(idsA: Array<string>, idsB: Array<string>): number {
  const topA = idsA.slice(0, TOP_N)
  const topB = idsB.slice(0, TOP_N)

  if (topA.length === 0 || topB.length === 0) return 0

  const setB = new Set(topB)
  const missingCount = topA.filter(id => !setB.has(id)).length

  let displacementSum = 0
  for (const id of topA) {
    const indexInB = topB.indexOf(id)
    if (indexInB === -1) {
      displacementSum += TOP_N
    } else {
      displacementSum += Math.abs(topA.indexOf(id) - indexInB)
    }
  }

  const overlapScore = missingCount / TOP_N
  const displacementScore = displacementSum / (TOP_N * TOP_N)

  return (overlapScore + displacementScore) / 2
}

interface UseSalesAndCtrRankingResult {
  experimentVariant: string | undefined
  rankingComparisonPending: boolean
}

function useSalesAndCtrRanking(destinationId: string | undefined): UseSalesAndCtrRankingResult {
  const query = useQueryParams()
  const utmAdgroup = query.get('utm_adgroup')

  const isEligibleAdgroup = !!utmAdgroup && ELIGIBLE_AD_GROUP_IDS.has(utmAdgroup)
  const isBali = destinationId === BALI_DESTINATION_ID
  const shouldCompare = isEligibleAdgroup && !!destinationId && !isBali

  const baseFilters = useMemo((): App.OfferListFilters | undefined => {
    if (!shouldCompare) return undefined
    return {
      destinationId,
      offerTypes: HOTEL_OFFER_TYPES,
      limit: TOP_N,
    }
  }, [shouldCompare, destinationId])

  const filtersWithRollout = useMemo((): App.OfferListFilters | undefined => {
    if (!baseFilters) return undefined
    return {
      ...baseFilters,
      rollout: ['salesCtrRanking'],
    }
  }, [baseFilters])

  const withRolloutList = useOfferList(filtersWithRollout, { disabled: !shouldCompare })
  const withoutRolloutList = useOfferList(baseFilters, { disabled: !shouldCompare })

  const rankingComparisonPending = shouldCompare && (withRolloutList.fetching || withoutRolloutList.fetching)

  const rankingsDiffer = useMemo(() => {
    if (!shouldCompare || rankingComparisonPending) return false

    const difference = computeRankingDifference(withRolloutList.offerIds, withoutRolloutList.offerIds)
    return difference >= DIFFERENCE_THRESHOLD
  }, [shouldCompare, rankingComparisonPending, withRolloutList.offerIds, withoutRolloutList.offerIds])

  const canActivate = isEligibleAdgroup && !rankingComparisonPending && rankingsDiffer

  const experimentVariant = useOptimizelyExperiment(OptimizelyExperiments.searchSalesCtrPaidTrafficRanking, canActivate)

  return { experimentVariant, rankingComparisonPending }
}

export default useSalesAndCtrRanking
